import {
  makeFinalFormValidator,
  TypeFromObjectSchema,
} from '@readcloud/common';
import { BSForm, Button, FileCard } from '@readcloud/component-library';
import {
  Annotation,
  Book,
  ImageAnnotation,
  Tag,
  UserPrefState,
} from '@readcloud/data';
import React, { useEffect, useState } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import * as yup from 'yup';
import { Forms, ImageIcon } from '../..';
import { AnnotationsContext } from '../AnnotationsContext';
import MediaBrowserWrapper from '../MediaBrowserWrapper';
import C from 'classnames';
import { Input, Large } from '@readcloud/ui';

const schema = yup
  .object({
    url: yup.string().required().url().label('URL'),
    publisherOptions: Forms.commonSchemas.publisherOptions,
  })
  .shape(Forms.commonSchemas.commonFields);

const validate = makeFinalFormValidator(schema);
type SchemaValues = TypeFromObjectSchema<typeof schema>;

type EditFormRenderProps = FormRenderProps<SchemaValues>;
const EditorForm = (
  props: FormRenderProps<SchemaValues> & {
    handleSubmit: EditFormRenderProps['handleSubmit'];
    getState: EditFormRenderProps['form']['getState'];
    autoSubmit: boolean;
    onCancel?: () => void;
    submitLabel?: string;
    showPublisherOptions: boolean;
    mediaContent?: { mediaContent: Book[]; tagsContent: Tag[] };
    onSavePreferences?: (type: string, data: Annotation['data']) => void;
    annotation?: ImageAnnotation;
  }
) => {
  const [selectedFile, setSelectedFile] =
    useState<React.ComponentPropsWithoutRef<typeof FileCard>>();
  const [mediaBrowserOpen, setMediaBrowserOpen] = useState(false);
  const toggleMediaBrowser = () => {
    setMediaBrowserOpen((d) => !d);
  };
  const [hasLoadedValues, setHasLoadedValues] = useState(false);
  const onMediaBrowserSelect = (
    selection?: React.ComponentPropsWithoutRef<typeof FileCard>
  ) => {
    setMediaBrowserOpen(false);

    selection ? setSelectedFile(selection) : setSelectedFile(undefined);
    // set the URL of the field based on selection.
    // hide url field, instead show the title of the content passed back.
  };

  useEffect(() => {
    if (hasLoadedValues) {
      props.form.mutators.setUrl(
        selectedFile && selectedFile.filePath ? selectedFile.filePath : ''
      );
    }
    setHasLoadedValues(true);
  }, [selectedFile]);

  useEffect(() => {
    const tempURL = props.form.getFieldState('url');
    if (tempURL && tempURL.value) {
      const re = new RegExp(/api\.readcloud\.com/i);
      if (tempURL.value.match(re)) {
        props.mediaContent &&
          props.mediaContent.mediaContent.forEach((media) => {
            if (media.downloadURI && media.downloadURI === tempURL.value) {
              setSelectedFile({
                thumbnailUrl: media.imageThumbnailURI
                  ? media.imageThumbnailURI
                  : '/',
                fileName: media.title,
                filePath: media.downloadURI,
                created: media.created.toString(),
                fileUploader: media.authorName,
              });
            }
          });
      }
    }
  }, []);

  const state = props.getState();
  return (
    <>
      {selectedFile && selectedFile.fileName && (
        <>
          <div>
            <small className="rc-text-gray-500 rc-mb-0">Content</small>
            <div className="rc-flex rc-justify-between rc-items-start">
              <Large>{selectedFile.fileName}</Large>
              <Button
                variant="light"
                className="rc-ml-2"
                onClick={() => {
                  setSelectedFile(undefined);
                  props.form.mutators.setUrl('');
                }}
              >
                <i className="fe fe-x" />
              </Button>
            </div>
          </div>
        </>
      )}
      <form className="rc-flex rc-flex-col" onSubmit={props.handleSubmit}>
        <Forms.AutoSubmit enabled={props.autoSubmit} />
        {!selectedFile && (
          <Button onClick={toggleMediaBrowser} variant="light">
            <ImageIcon /> Browse Content
          </Button>
        )}
        <div
          className={C(
            'rc-flex rc-flex-row rc-text-center rc-items-center',
            !!selectedFile && 'rc-hidden'
          )}
        >
          <div className="rc-w-1/3">
            <hr />
          </div>
          <div className="rc-w-1/3">Or</div>
          <div className="rc-w-1/3">
            <hr />
          </div>
        </div>
        <Field
          name="url"
          label="URL"
          hidden={!!selectedFile}
          render={(field) => (
            <div
              className={C(
                'readcloud-ui readcloud-ui-reset',
                !!selectedFile && 'rc-hidden'
              )}
            >
              <Input placeholder="URL" {...field.input} />
            </div>
          )}
        ></Field>
        <div className="rc-py-2"></div>
        <div className="rc-flex rc-flex-col">
          <Forms.commonElements.CommonFields
            showPublisherOptions={props.showPublisherOptions}
          />
        </div>

        {!props.autoSubmit && (
          <div className="rc-flex rc-justify-end rc-items-center rc-w-full">
            {props.onCancel ? (
              <>
                <Button variant="light" onClick={props.onCancel}>
                  Cancel
                </Button>{' '}
                <div className="rc-p-2" />
              </>
            ) : null}

            <Button
              disabled={!state.valid || state.submitting}
              submit
              variant="primary"
            >
              {props.submitLabel || 'Save'}
            </Button>
          </div>
        )}
      </form>
      <MediaBrowserWrapper
        mediaType={['IMAGE']}
        toggle={toggleMediaBrowser}
        isOpen={mediaBrowserOpen}
        onSelect={onMediaBrowserSelect}
        content={props.mediaContent}
      ></MediaBrowserWrapper>
    </>
  );
};

export function ImageAnnotationEditor(props: {
  autoSubmit?: boolean;
  submitLabel?: string;
  showPublisherOptions: boolean;
  annotation: ImageAnnotation;
  onChange: (annotation: ImageAnnotation) => void;
  onCancel?: () => void;
  mediaContent?: { mediaContent: Book[]; tagsContent: Tag[] };
  onSavePreferences?: (type: string, data: Annotation['data']) => void;
  annoPrefs?: UserPrefState['annoPrefs'];
}) {
  const handleSubmit = React.useCallback(
    (values: TypeFromObjectSchema<typeof schema>) => {
      props.onChange(
        Forms.commonElements.formValuesToAnnotation(props.annotation, values)
      );
    },
    [props.onChange, props.annotation]
  );

  const annotationsContext = React.useContext(AnnotationsContext);

  const [prefValues, setPrefValues] = useState(
    props.annoPrefs && props.annotation
      ? props.annoPrefs[props.annotation.type]
      : undefined
  );

  useEffect(() => {
    setPrefValues(
      props.annoPrefs && props.annotation
        ? props.annoPrefs[props.annotation.type]
        : undefined
    );
  }, [props.annoPrefs, props.annotation.type]);

  return (
    <Form
      validate={validate}
      onSubmit={handleSubmit}
      initialValues={
        Forms.commonElements.annotationToFormValues(props.annotation).data ||
        prefValues
      }
      //the when the initial form values changed becuase of the form auto-submitting on any onChange, it would reinitialise and cause another onChange event to trigger - causing an infinite loop.
      //the only reason this wasn't crashing the app is because the autosubmit is on a 300ms debounce.
      //these 2 props seem to be required to stop that from happening.
      keepDirtyOnReinitialize
      //The initalValuesEqual prop is meant to take a deepEquals function that it uses on the previous/current values. Since we don't use the same form for different annotations
      //(we dont swap another anno's values into the current form at any point), I think it's safe to assume that the previous values should always be the same as the current values here,
      // and we can save on some computation.
      initialValuesEqual={() => true}
      mutators={{
        setUrl: ([url], state, utils) => {
          utils.changeValue(state, 'url', () => url);
        },
      }}
    >
      {(formProps) => (
        <EditorForm
          {...formProps}
          handleSubmit={formProps.handleSubmit}
          getState={formProps.form.getState}
          showPublisherOptions={props.showPublisherOptions}
          submitLabel={props.submitLabel}
          onCancel={props.onCancel}
          autoSubmit={!!props.autoSubmit}
          mediaContent={annotationsContext && annotationsContext}
          onSavePreferences={props.onSavePreferences}
          annotation={props.annotation}
        />
      )}
    </Form>
  );
}
